import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, FormLabel, lighten, styled } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  DateTimePicker as MuiDateTimePicker,
  DateTimePickerProps as MuiDateTimePickerProps,
} from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type {
  DateTimeValidationError,
  FieldSelectedSections,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers/models';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import preParsePostFormat from 'dayjs/plugin/preParsePostFormat';
import 'dayjs/locale/de';
import 'dayjs/locale/ar';
import { useTranslation } from '@askporter/config-provider';
import {
  claimEntireFieldSectionOnPointerDown,
  selectEntireFieldSectionOnPointerUp,
} from '../DatePicker/selectEntireFieldSectionOnPointerUp';
import { IconSize, Icon } from '../Icon';

dayjs.extend(localeData);
dayjs.extend(preParsePostFormat);
dayjs.extend(customParseFormat);

const DATETIME_OUTPUT_FORMAT = 'YYYY-MM-DD HH:mm';

/**
 * Notifies the parent only for an explicit clear (`null`) or a valid Dayjs.
 * Incomplete/invalid mid-edit values are ignored so controlled parents (e.g. RJSF)
 * do not sync an empty value back and wipe the field.
 */
export const commitDateTimeValue = (newValue: Dayjs | null, onChange: (value: string) => void): void => {
  if (newValue === null) {
    onChange('');
    return;
  }

  if (!newValue.isValid()) {
    return;
  }

  // Use 'en' locale for the output format to ensure ASCII digits regardless of display locale.
  // RTL locales like Arabic use Arabic-Indic numerals via the preParsePostFormat plugin.
  onChange(newValue.locale('en').format(DATETIME_OUTPUT_FORMAT));
};

const isFocusInsideDateTimePicker = (root: HTMLElement | null, target: EventTarget | null): boolean => {
  if (!root || !(target instanceof Node)) {
    return false;
  }
  if (root.contains(target)) {
    return true;
  }
  if (target instanceof Element) {
    return Boolean(target.closest('.MuiPickersPopper-root, [role="dialog"]'));
  }
  return false;
};

/** Stable slot identity — inline slot components remount the field on every render and steal focus. */
const OpenPickerIcon = () => (
  <Icon size={IconSize.MD} folder="design-system/calendar" customStyles={{ pointerEvents: 'none' }} />
);

const dateTimePickerSlots = {
  openPickerIcon: OpenPickerIcon,
};

export interface DateTimePickerProps
  extends Omit<
    MuiDateTimePickerProps,
    'onChange' | 'onAccept' | 'value' | 'maxDate' | 'minDate' | 'maxTime' | 'minTime'
  > {
  value: string | null;
  onChange: (value: string) => void;
  /**
   * Called when the user accepts a value from the picker UI (calendar/clock/OK).
   * Receives '' on clear, or a valid YYYY-MM-DD HH:mm string.
   */
  onAccept?: (value: string) => void;
  /**
   * Called when focus leaves the field entirely (not when moving between sections).
   * Receives the committed YYYY-MM-DD HH:mm string (or '').
   */
  onBlur?: (value: string) => void;
  label?: string;
  formLabel?: string;
  maxDate?: string | null;
  minDate?: string | null;
  maxTime?: string | null;
  minTime?: string | null;
  id?: string;
}

const StyledDateTimePicker = styled(MuiDateTimePicker)(({ theme }) => ({
  '&.MuiFormControl-root': {
    marginTop: 0,
  },
  '.MuiPickersInputBase-root': {
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#414651',
    WebkitTextFillColor: '#414651',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    '&:focus-within': {
      boxShadow: `0 0 0 1px ${lighten(theme.palette.primary.main, 0.4)}, 0 0 0 4px ${lighten(
        theme.palette.primary.main,
        0.8,
      )}`,
      '&.Mui-error': {
        boxShadow: `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(240, 68, 56, 0.24)`,
      },
      backgroundColor: '#fff',
      outline: 'none',
      borderRadius: '8px !important',
    },
    // Widget/page borders own the outline — hide the MUI notched fieldset so it does not
    // show as a small rectangle beside the calendar icon when a section is focused.
    '& .MuiPickersOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiPickersSectionList-root': {
      padding: '12px 0',
      marginLeft: '12px',
    },
    '& [role="spinbutton"]': {
      userSelect: 'all',
      WebkitUserSelect: 'all',
    },
    flexDirection: 'row-reverse',
    '& .MuiInputAdornment-root': {
      marginLeft: 0,
      '& .MuiIconButton-root': {
        marginLeft: 0,
      },
    },
  },
}));

const parseHHmm = (t?: string | null): Dayjs | null => (t ? dayjs(t, 'HH:mm', true) : null);

const parseDateTimeValue = (value: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

/**
 * Uses `defaultValue` + remount key instead of a controlled `value` while typing.
 * Updating controlled `value` on every section keystroke (especially a partial year)
 * re-renders the field and wipes mid-edit content.
 */
export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  onAccept,
  onBlur,
  label,
  formLabel,
  maxDate,
  minDate,
  minTime,
  maxTime,
  slotProps: consumerSlotProps,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  const [selectedSections, setSelectedSections] = useState<FieldSelectedSections>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const isFocusedRef = useRef(false);
  const draftRef = useRef<Dayjs | null>(parseDateTimeValue(value));
  const lastCommittedValueRef = useRef<string | null>(value);
  const onChangeRef = useRef(onChange);
  const onAcceptRef = useRef(onAccept);
  const onBlurRef = useRef(onBlur);
  onChangeRef.current = onChange;
  onAcceptRef.current = onAccept;
  onBlurRef.current = onBlur;

  const { i18n } = useTranslation();
  const adapterLocale = i18n.language ? i18n.language.split('-')[0] : 'en';
  const format = adapterLocale === 'de' ? 'DD. MMM YYYY HH:mm' : 'DD MMM YYYY HH:mm';

  // Remount with a fresh defaultValue only when the parent value changes while not editing.
  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }
    if (value === lastCommittedValueRef.current) {
      return;
    }
    lastCommittedValueRef.current = value;
    draftRef.current = parseDateTimeValue(value);
    setPickerKey((key) => key + 1);
  }, [value]);

  const commitDraft = (next: Dayjs | null, notifyAccept: boolean) => {
    draftRef.current = next;
    commitDateTimeValue(next, (committed) => {
      lastCommittedValueRef.current = committed || null;
      onChangeRef.current(committed);
      if (notifyAccept) {
        onAcceptRef.current?.(committed);
      }
    });
  };

  const handleChange = (newValue: Dayjs | null) => {
    // No setState — avoids re-render/remount while MUI advances day → month → year → time.
    isFocusedRef.current = true;
    draftRef.current = newValue;
  };

  /**
   * MUI X v8 field edits call setValue with default changeImportance `'accept'`, so `onAccept`
   * also fires when a section completes while typing. Only treat picker-UI acceptance as onAccept.
   */
  const handleAccept = (accepted: Dayjs | null, context: PickerChangeHandlerContext<DateTimeValidationError>) => {
    if (context.source !== 'view') {
      return;
    }
    commitDraft(accepted, true);
  };

  const commitLeave = () => {
    if (!isFocusedRef.current) {
      return;
    }

    isFocusedRef.current = false;
    setSelectedSections(null);
    const previousCommittedValue = lastCommittedValueRef.current ?? '';
    let committedValue = '';
    commitDateTimeValue(draftRef.current, (committed) => {
      committedValue = committed;
      // onAccept may have already committed this exact value; avoid re-notifying onChange.
      if (committed !== previousCommittedValue) {
        lastCommittedValueRef.current = committed || null;
        onChangeRef.current(committed);
      }
    });
    onBlurRef.current?.(committedValue);
  };

  const handleContainerBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.relatedTarget) {
      return;
    }
    if (isFocusInsideDateTimePicker(rootRef.current, event.relatedTarget)) {
      return;
    }
    commitLeave();
  };

  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      if (!isFocusedRef.current) {
        return;
      }
      if (isFocusInsideDateTimePicker(rootRef.current, event.target)) {
        return;
      }
      commitLeave();
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (!isFocusedRef.current) {
        return;
      }
      if (isFocusInsideDateTimePicker(rootRef.current, event.target)) {
        return;
      }
      commitLeave();
    };

    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only listeners
  }, []);

  const referenceDate = useMemo(() => {
    const today = dayjs();
    const parsedMinTime = parseHHmm(minTime);
    if (parsedMinTime) {
      return today.hour(parsedMinTime.hour()).minute(parsedMinTime.minute()).second(0).millisecond(0);
    }
    return today;
    // dayjs() is intentional "now" once per minTime identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minTime]);

  const slotProps = useMemo(() => {
    const consumerTextField =
      consumerSlotProps?.textField && typeof consumerSlotProps.textField === 'object'
        ? consumerSlotProps.textField
        : {};

    return {
      ...consumerSlotProps,
      textField: {
        ...consumerTextField,
        inputProps: {
          'aria-label': label,
          ...('inputProps' in consumerTextField &&
          typeof consumerTextField.inputProps === 'object' &&
          consumerTextField.inputProps
            ? consumerTextField.inputProps
            : {}),
        },
        readOnly: props.readOnly,
        id: props.id,
        sx: {
          marginTop: 0,
          marginBottom: 0,
          ...('sx' in consumerTextField && typeof consumerTextField.sx === 'object' && consumerTextField.sx
            ? consumerTextField.sx
            : {}),
        },
      },
      openPickerButton: {
        'aria-label': label,
        ...(typeof consumerSlotProps?.openPickerButton === 'object' ? consumerSlotProps.openPickerButton : {}),
      },
      dialog: {
        'aria-label': label,
        ...(typeof consumerSlotProps?.dialog === 'object' ? consumerSlotProps.dialog : {}),
      },
      popper: {
        'aria-label': label,
        ...(typeof consumerSlotProps?.popper === 'object' ? consumerSlotProps.popper : {}),
      },
    };
  }, [consumerSlotProps, label, props.readOnly, props.id]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      {formLabel && (
        <FormLabel
          sx={{
            ml: '2px',
            mb: '4px',
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
            color: '#344054',
          }}
        >
          {formLabel}
        </FormLabel>
      )}
      <Box
        ref={rootRef}
        sx={{ display: 'inline-flex', width: '100%' }}
        onFocus={() => {
          isFocusedRef.current = true;
        }}
        onBlur={handleContainerBlur}
        onPointerDownCapture={(event) => {
          const sectionIndex = claimEntireFieldSectionOnPointerDown(event, rootRef.current);
          if (sectionIndex !== null) {
            isFocusedRef.current = true;
            setSelectedSections(sectionIndex);
          }
        }}
        onPointerUp={(event) => selectEntireFieldSectionOnPointerUp(event, rootRef.current)}
      >
        <StyledDateTimePicker
          {...props}
          key={pickerKey}
          defaultValue={parseDateTimeValue(value)}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={handleChange}
          onAccept={handleAccept}
          selectedSections={selectedSections}
          onSelectedSectionsChange={setSelectedSections}
          disabled={props.disabled}
          format={format}
          slots={dateTimePickerSlots}
          slotProps={slotProps}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          minDate={minDate ? dayjs(minDate) : undefined}
          minTime={parseHHmm(minTime) ?? undefined}
          maxTime={parseHHmm(maxTime) ?? undefined}
          referenceDate={referenceDate}
          ampm={false}
        />
      </Box>
    </LocalizationProvider>
  );
};
